
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, FlatList, Keyboard } from 'react-native';
import { Icons } from '../../Components';
import Colors from '../../constants/colors';
import { button_styles } from '../../constants/styles';
import { ProfileApi, ProfileHelpers } from '../api';
import type { FocusPositionProps, MyPlayerProps } from '../../types';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';


type PhoneManagerProps = {
    player:MyPlayerProps,
    walkthrough?:boolean,
    onNext:() => void,
    onFocusPosition?:(data:FocusPositionProps) => void,
    onClose:() => void,
    onPlayerUpdate:(player:MyPlayerProps) => void
}
const sections = ['header','phone'];
const PhoneManager = ({ player, walkthrough, onPlayerUpdate, onFocusPosition, onNext, onClose }:PhoneManagerProps) => {
    const C = useColors();
    const [ loading, setLoading ] = useState(false);
    const [ verify_token, setVerifyToken ] = useState({
        loading:false,
        visible: false,
        token: ''
    })
    const [ draft_player, setDraftPlayer ] = useState<MyPlayerProps>(player)
    const [ update_error, setUpdateError ] = useState<string>();
    const [ verify_error, setVerifyError ] = useState<string>();

    useEffect(() => {
        setDraftPlayer(player);
    },[player])


    const handlePhoneChange = (text:string) => {
        if(update_error){ setUpdateError(undefined) }
        let phone = ProfileHelpers.unformatPhone(text)
        setDraftPlayer({ ...draft_player, phone })
    }

    const { valid, error } = ProfileHelpers.isPhoneValid(draft_player.phone)

    const is_changed = draft_player.phone.toLowerCase() != player.phone.toLowerCase() ? true : false
    const token_valid = verify_token.token != '' ? true : false
    const update_allowed = valid && is_changed ? true : false
    const handleUpdatePlayer = async() => {
        try {
            setLoading(true);
            await ProfileApi.updatePlayerPhone(draft_player.phone);
            setTimeout(() => {
                setLoading(false);
                setVerifyToken({
                    loading:false,
                    visible:true,
                    token: ''
                })
            }, 1000);
        } catch (e) {
            setLoading(false);
            setUpdateError('Unable to use this phone number.  Please try again')
        }
    }

    const handleNext = async() => {
        if(valid && !update_allowed){ return onNext() }
        if(!update_allowed){ return }
        await handleUpdatePlayer()
    }

    const handleVerifyPhone = async() => {
        try {
            setVerifyToken({ ...verify_token, loading:true });
            const player = await ProfileApi.confirmPlayerPhone(verify_token.token);
            setTimeout(() => {
                setLoading(false);
                setVerifyToken({
                    loading:false,
                    visible:false,
                    token: ''
                })
                if(walkthrough){ onNext() } else { onClose() }
            }, 1000);
            onPlayerUpdate(player)
        } catch (e) {
            setVerifyToken({ ...verify_token, loading:false });
            setVerifyError('Invalid Token.  Please try again')
        }
    }

    const handleCancelUpdate = () => {
        setVerifyToken({ visible:false, loading:false, token: '' })
        setDraftPlayer(player)
    }

    const renderSections = (data: { item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View style={{ padding:20 }}>
                        <Text size={26} theme='h1'>Confirm Your Phone Number</Text>
                        <Text style={{ marginTop:15 }} size={14} theme='h2'>Confirming your phone number will help secure your account and is required for real-money activities</Text>
                    </View>
                )
            case 'phone':
                return (
                    <View style={{ padding:30 }}>
                        <View type='input' style={{ flexDirection:'row', alignItems:'center' }}>
                            <View transparent style={{ paddingLeft:15, paddingRight:15 }}>
                                <Icons.PhoneIcon
                                    color={C.text.h1}
                                    size={18}
                                />
                            </View>
                            <TextInput
                                value={ProfileHelpers.formatPhone(draft_player.phone)}
                                keyboardType='phone-pad'
                                autoFocus
                                editable={player.vouched_status == 'unverified' ? true : false}
                                onFocusPosition={onFocusPosition}
                                style={{ flex:1 }}
                                onChangeText={(text) => handlePhoneChange(text)}
                            />
                            <Button transparent style={{ padding:0, paddingLeft:15, paddingRight:15 }} onPress={() => Keyboard.dismiss()}>
                                {loading ?
                                <ActivityIndicator size='small' color={C.text.h1}/>
                                :
                                <View>
                                    { valid ?
                                    <Icons.CheckIcon size={16} color={C.text.success} />
                                    : verify_token.visible ?
                                    <Icons.AlertIcon size={16} color={C.text.warning} />
                                    :
                                    <Icons.CloseIcon size={14} color={C.text.error} />
                                    }
                                </View>
                                }
                            </Button>
                        </View>
                        {error ?
                        <Text style={{ marginTop:10 }} theme='warning'>{error}</Text>
                        :<></>}
                        {update_error ?
                        <Text style={{ marginTop:10 }} theme='warning'>{update_error}</Text>
                        :<></>}
                        {player.vouched_status !== 'unverified' && (
                            <View style={{ marginTop: 15, padding: 15, backgroundColor:'rgba(255, 193, 7, 0.1)', borderRadius: 8, borderWidth: 1, borderColor: C.absolutes.utility.warning }}>
                                <View transparent style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                    <Icons.AlertIcon size={20} color={C.text.warning}/>
                                    <View transparent style={{ flex: 1, marginLeft:10 }}>
                                        <Text theme='h2' size={14} style={{ marginBottom: 8 }}>Phone Update Restricted</Text>
                                        <Text theme='description' size={12} style={{ lineHeight: 18 }}>
                                            Your account verification status prevents phone updates. Please contact support for assistance with changing your phone number.
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        )}
                    </View>
                )
            default: return <></>
        }
    }

    return (
            <View style={{ flex:1 }}>
                <View style={{ flex:1 }}>
                    <FlatList
                        data={sections}
                        key={'phone_sections'}
                        keyExtractor={item => item}
                        renderItem={renderSections}
                    />
                </View>
                {walkthrough ?
                <View nativeID='action_row' style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }}>
                    <Button
                        title='EXIT SETUP'
                        type='text'
                        onPress={() => onClose()}
                    />
                    <Button 
                        style={valid && !loading ? button_styles.wizard_valid : button_styles.wizard_invalid}
                        disabled={!valid || loading}
                        onPress={() => handleNext()}
                        >
                        <Icons.ChevronIcon size={20} direction='right' color={!valid ? C.text.error : C.text.success}/>
                    </Button>
                </View>
                :
                <View nativeID='action_row' style={{ flexDirection:'row', justifyContent:'space-between', padding:10 }}>
                    <Button
                        style={{ flex:1, marginRight:5 }}
                        title='BACK'
                        type='close'
                        onPress={() => onClose()}
                    />
                    <Button
                        style={{ flex:3, opacity:valid && !loading && update_allowed ? 1:0.5 }}
                        disabled={!valid || loading || !update_allowed}
                        loading={loading}
                        title='UPDATE'
                        type='success'
                        onPress={async() => {
                            await handleUpdatePlayer()
                        }}
                    />
                </View>
                }
                {verify_token.visible ?
                 <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                    <View float style={{ padding:20, minWidth:240 }}>
                        <View transparent style={{ padding:10 }}>
                            <Text size={14} theme='h1'>Enter Code Sent To</Text>
                            <Text size={18} style={{ marginTop:10 }} theme='h2'>{draft_player.phone}</Text>
                        </View>
                        <View type='input' style={{ flexDirection:'row', alignItems:'center' }}>
                            <TextInput
                                value={verify_token.token}
                                textContentType='oneTimeCode'
                                onFocusPosition={onFocusPosition}
                                style={{ flex:1, textAlign:'center'}}
                                placeholder='00000'
                                onChangeText={(text) => setVerifyToken({ ...verify_token, token: text })}
                            />
                        </View>
                        {verify_error ?
                            <Text size={14} color={Colors.utility.warning}>{error}</Text>
                        :<></>}
                        <Button
                            title='CONFIRM'
                            style={{ marginTop:20, opacity:token_valid && !loading ? 1 : 0.5 }}
                            disabled={!token_valid || loading}
                            loading={verify_token.loading}
                            type='success'                            
                            onPress={() => handleVerifyPhone()}
                        />
                        <Button
                            title='CANCEL'
                            style={{ marginTop:10 }}
                            disabled={loading}
                            loading={verify_token.loading}
                            type='close'
                            onPress={() => handleCancelUpdate()}
                        />
                    </View>
                </View>
                :<></>}
                </View>
    )
}

export default PhoneManager